import React, { useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCcw } from "lucide-react";
import { listAutoReplyCommercePolicyProfiles } from "../../../api/autoReplyCommercePolicyProfiles";
import { validatePrepareCommerceServiceConfig } from "../utils/prepareCommerceServiceValidation";

const inputClass = "mt-2 min-h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-800 focus:border-emerald-600 focus:outline-none";

const readError = error =>
  error?.response?.data?.message || error?.message ||
  "Order Policy Profiles could not be loaded.";

export default function PrepareCommerceServiceEditor({ node, onClose, onSave }) {
  const storedConfig = node?.data?.config || {};
  const storedProfileId = String(storedConfig.commerceOrderPolicyProfileId || "").trim();
  const [selectedProfileId, setSelectedProfileId] = useState(storedProfileId);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const loadProfiles = async () => {
    setLoading(true);
    setLoadError("");
    try {
      setProfiles(await listAutoReplyCommercePolicyProfiles(true));
    } catch (error) {
      setLoadError(readError(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProfiles(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedProfile = useMemo(
    () => profiles.find(profile => String(profile.id) === selectedProfileId) || null,
    [profiles, selectedProfileId],
  );
  const selectedStatus = !selectedProfileId
    ? "default"
    : selectedProfile
      ? (selectedProfile.isActive ? "active" : "inactive")
      : "missing";
  const config = {
    schemaVersion: 2,
    commerceOrderPolicyProfileId: selectedProfileId || null,
  };
  const validation = validatePrepareCommerceServiceConfig(config, {
    commerceOrderPolicyProfileStatus: selectedStatus,
  });
  const selectableProfiles = profiles.filter(profile =>
    profile.isActive && (!profile.isDefault || String(profile.id) === selectedProfileId));

  const save = () => {
    if (loading || loadError || !validation.isValid) return;
    onSave?.({
      ...node,
      data: {
        ...(node.data || {}),
        config,
        commerceOrderPolicyProfileDisplayName: selectedProfile?.name || "",
        commerceOrderPolicyProfileStatus: selectedProfileId ? "active" : "default",
      },
    });
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[1001] flex items-center justify-center overflow-y-auto bg-black/10 p-4 sm:p-6">
      <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-xl">
        <h2 className="text-lg font-bold">Prepare Commerce Service</h2>
        <p className="mt-1 text-sm text-slate-500">
          Prepare the booked service for Commerce review.
        </p>
        <div className="mt-5 rounded-lg border border-slate-200 p-4">
          <label htmlFor="prepare-commerce-policy" className="text-sm font-semibold text-slate-800">
            Order Policy Profile
          </label>
          {loading ? (
            <div role="status" className="mt-2 flex min-h-10 items-center text-sm text-slate-500">
              <Loader2 size={16} className="mr-2 animate-spin" /> Loading policies
            </div>
          ) : loadError ? (
            <div role="alert" className="mt-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
              <p>{loadError}</p>
              <button type="button" onClick={loadProfiles} className="mt-2 inline-flex items-center gap-2 font-semibold">
                <RefreshCcw size={14} /> Retry
              </button>
            </div>
          ) : (
            <select id="prepare-commerce-policy" className={inputClass} value={selectedProfileId} onChange={event => setSelectedProfileId(event.target.value)}>
              <option value="">Use business default</option>
              {selectedStatus === "missing" ? <option value={selectedProfileId} disabled>Previously selected policy unavailable</option> : null}
              {selectedStatus === "inactive" ? <option value={selectedProfileId} disabled>{selectedProfile.name} (Inactive)</option> : null}
              {selectableProfiles.map(profile => <option key={profile.id} value={profile.id}>{profile.name}{profile.isDefault ? " (Default policy)" : ""}</option>)}
            </select>
          )}
          <p className="mt-2 text-xs leading-5 text-slate-500">
            Controls fulfilment and pricing rules used for the Commerce Order created from this service.
          </p>
          {!loading && !loadError && !validation.isValid ? <p role="alert" className="mt-2 text-sm text-rose-700">{validation.message}</p> : null}
        </div>
        <dl className="mt-4 grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm sm:grid-cols-2">
          <div><dt className="font-semibold text-slate-700">Source</dt><dd className="text-slate-600">Scheduling Offering</dd></div>
          <div><dt className="font-semibold text-slate-700">Commerce transaction</dt><dd className="text-slate-600">Booking</dd></div>
          <div><dt className="font-semibold text-slate-700">Quantity</dt><dd className="text-slate-600">1 service</dd></div>
          <div><dt className="font-semibold text-slate-700">Commercial identity</dt><dd className="text-slate-600">Exact linked Catalogue Service Variant</dd></div>
        </dl>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded border px-4 py-2" onClick={onClose}>Cancel</button>
          <button type="button" disabled={loading || Boolean(loadError) || !validation.isValid} className="rounded bg-emerald-600 px-4 py-2 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50" onClick={save}>Save node</button>
        </div>
      </div>
    </div>
  );
}
